<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <h4 class="mt-6 mb-8">
        Tell Us About Your Company
      </h4>
      <p class="p3 mb-9">
        Please provide us with some additional information about your company.
      </p>
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <DpplTextField
          v-model="company"
          label="Company Name"
          :rules="companyRules"
          validate-on="blur"
        />
        <DpplSelect
          v-model="ats"
          :items="atsSelectItems"
          label="ATS"
          :rules="atsRules"
          validate-on="blur"
          item-title="name"
          item-value="id"
          variant="outlined"
        />
        <DpplTextField
          v-model="url"
          class="mb-3"
          label="Link to Your Job Board"
          :rules="urlRules"
          validate-on="blur"
          :hint="urlHint"
        >
          <template #append-inner>
            <DpplTooltip
              tooltip-placement="top"
              max-width="180px"
            >
              <template #activator="{ onMouseenter, onMouseleave }">
                <img
                  class="info-icon"
                  src="@/assets/icon_info.png"
                  alt="Info icon"
                  @mouseenter="onMouseenter"
                  @mouseleave="onMouseleave"
                >
              </template>
              <template #tooltip-content>
                <span>
                  Provide a link to an active job board hosted on your company's site.
                  For example: Datapeople.co/Careers
                </span>
              </template>
            </DpplTooltip>
          </template>
        </DpplTextField>
        <DpplSignupFormButton
          type="submit"
          block
          :loading="loading"
        >
          Submit
        </DpplSignupFormButton>
      </v-form>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import { signupAtsOptions, signupIsBilling, signupSubmitAtsDemoRequest } from '@/api/signup';
import validation from '@/helpers/validation';
import DpplSelect from '@/components/datapeople/DpplSelect.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';

export default {
  name: 'AboutYourCompany',
  components: {
    WizardContainer,
    WizardContent,
    DpplSelect,
    DpplTextField,
    DpplSignupFormButton,
    DpplTooltip,
  },
  emits: ['track-page-load'],
  data() {
    return {
      company: '',
      companyRules: [
        (v) => !validation.isEmpty(v) || 'Company name is required',
        (v) => (v && v.length <= 250) || 'Company name is too long',
      ],
      ats: '',
      atsRules: [
        (v) => !!v || 'ATS is required',
      ],
      url: '',
      urlRules: [
        (v) => !validation.isEmpty(v) || 'Url is required',
        (v) => validation.jobboardUrlBlacklist(v) || 'Please provide a valid URL to a job board'
          + ' hosted on your company’s website.',
        (v) => validation.url(v) || 'Url format is invalid',
        (v) => (v && v.length <= 250) || 'Url is too long',
      ],
      firstName: this.$route.params.firstName,
      projectId: this.$route.params.projectId,
      valid: false,
      loading: false,
      atsSelectItems: [],
    };
  },
  computed: {
    urlHint() {
      return this.url && !validation.hasSubdomainOrPath(this.url)
        ? 'This URL does not appear to be a link to an active job board. Please confirm that'
          + ' you’re providing a link to your company’s open jobs.'
        : null;
    },
  },
  async created() {
    const getAtsList = async () => {
      const atsOptions = await signupAtsOptions();
      this.atsSelectItems = atsOptions;
    };
    const registerConversion = async () => {
      const responseJSON = await signupIsBilling();
      if (responseJSON.success === true && responseJSON.isBilling === false) {
        this.$gtm.trackEvent({ event: 'conversion' });
      }
    };
    Promise.all([getAtsList(), registerConversion()]).catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Demo About Your Company' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupSubmitAtsDemoRequest({
            projectId: this.projectId,
            company: this.company,
            url: this.url,
            ats: this.ats,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'demo-request-received',
              params: {
                firstName: this.firstName,
              },
            });
          }
        }
      } catch (e) {
        this.updateFlash({ message: 'Something went wrong' });
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped lang="less">
  .info-icon {
    height: 15px;
    margin-top: 5px;
    margin-right: 3px;
  }

.form {
  width: 100%;
}
</style>
